import { useEffect, useState } from "react";
import { MessageCircle, Copy, ExternalLink, Search, CheckCircle2, AlertTriangle, RefreshCw, Filter, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STEPS = [
  { k: "j-2", l: "J-2 · Préparez votre venue" },
  { k: "j-1", l: "J-1 · Demain !" },
  { k: "j-0", l: "Jour J · Aujourd'hui" },
];

export default function WhatsAppAdmin() {
  const [step, setStep] = useState("j-2");
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [normPreview, setNormPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api.get(`/admin/whatsapp/recipients?step_key=${step}`)
       .then((r) => setData(r.data))
       .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [step]);

  const dryRunNorm = async () => {
    const r = await api.post("/admin/phones/normalize?dry_run=true");
    setNormPreview(r.data);
  };

  const applyNorm = async () => {
    if (!confirm(`Appliquer la normalisation à ${normPreview?.total_changed || 0} numéros ?`)) return;
    const r = await api.post("/admin/phones/normalize?dry_run=false");
    toast.success(`${r.data.total_changed} numéros normalisés`);
    setNormPreview(null);
    load();
  };

  const markSent = async (regNum) => {
    await api.post("/admin/whatsapp/mark-sent", { registration_number: regNum, step_key: step });
    load();
  };

  const copyMsg = (msg) => {
    navigator.clipboard.writeText(msg);
    toast.success("Message copié");
  };

  const recipients = (data?.recipients || []).filter((r) => {
    if (onlyMissing && r.marked_sent_wa) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [r.first_name, r.last_name, r.phone_e164, r.email, r.registration_number]
      .some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  return (
    <div data-testid="whatsapp-admin">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="eyebrow text-gold-dark flex items-center gap-2">
            <MessageCircle className="w-3.5 h-3.5" /> Campagne WhatsApp (semi-manuelle)
          </div>
          <h2 className="font-serif text-2xl text-ink mt-1">Rappels WhatsApp</h2>
          <p className="text-xs text-foreground/55 mt-1 max-w-2xl">
            Génère des messages WhatsApp personnalisés. Cliquez « Envoyer WA » pour ouvrir WhatsApp Web/mobile
            avec le message pré-rempli — c'est gratuit et illimité.
          </p>
        </div>
        <button onClick={load} className="btn-outline text-xs" data-testid="wa-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
        </button>
      </div>

      {/* Normalisation banner */}
      {!normPreview ? (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <strong>Numéros au format local ?</strong> Certains numéros sont peut-être enregistrés sans indicatif (ex. 06… au lieu de +336…).
            Vérifiez et normalisez en 1 clic.
          </div>
          <button onClick={dryRunNorm} className="btn-outline text-xs whitespace-nowrap" data-testid="wa-norm-preview">
            Prévisualiser
          </button>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <strong>{normPreview.total_changed} numéros</strong> peuvent être normalisés au format international (+33, +32, +31, +352…).
            </div>
          </div>
          {normPreview.preview.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-blue-700">Voir les {Math.min(30, normPreview.preview.length)} premiers exemples</summary>
              <div className="mt-2 grid gap-1 max-h-40 overflow-y-auto font-mono text-[0.7rem]">
                {normPreview.preview.map((p, i) => (
                  <div key={i}>{p.registration_number}: <span className="text-red-600">{p.before}</span> → <span className="text-emerald-600">{p.after}</span></div>
                ))}
              </div>
            </details>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setNormPreview(null)} className="btn-outline text-xs">Annuler</button>
            <button onClick={applyNorm} className="btn-primary text-xs" data-testid="wa-norm-apply">Appliquer la normalisation</button>
          </div>
        </div>
      )}

      {/* Stats + Filtres */}
      <div className="bg-white border border-border rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-foreground/55 mb-1.5">Palier</label>
          <select value={step} onChange={(e) => setStep(e.target.value)} data-testid="wa-step"
                  className="border border-border rounded-sm p-2.5 text-sm bg-white">
            {STEPS.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-foreground/55 mb-1.5">🔍 Recherche</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="nom, téléphone, email…"
                 data-testid="wa-search"
                 className="w-full border border-border rounded-sm p-2.5 text-sm" />
        </div>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="wa-only-missing" />
          Masquer les envoyés
        </label>
        <div className="ml-auto text-sm text-foreground/65">
          <strong>{data?.total || 0}</strong> destinataires · <strong className="text-emerald-600">{data?.valid_phones || 0}</strong> tél. valides
        </div>
      </div>

      {/* Recipients */}
      {busy ? (
        <div className="text-center text-foreground/55 py-10">Chargement…</div>
      ) : (
        <div className="space-y-2">
          {recipients.map((r) => (
            <div key={r.registration_number} className="bg-white border border-border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                 data-testid={`wa-row-${r.registration_number}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-ink">{r.first_name} {r.last_name}</span>
                  <span className="text-[0.65rem] font-mono bg-ink/5 px-1.5 py-0.5 rounded">{r.registration_number}</span>
                  {r.marked_sent_wa && <span className="text-[0.6rem] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">WA envoyé</span>}
                  {!r.has_valid_phone && <span className="text-[0.6rem] uppercase tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Tél. invalide</span>}
                </div>
                <div className="text-xs text-foreground/60 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="font-mono">{r.phone_e164 || r.phone_raw || "—"}</span>
                  <span>{r.email}</span>
                  {r.selected_stage && <span>• {r.selected_stage}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => copyMsg(r.message)} className="px-3 py-2 text-xs border border-border hover:border-ink rounded-sm inline-flex items-center gap-1.5"
                        data-testid={`wa-copy-${r.registration_number}`} title="Copier le message">
                  <Copy className="w-3.5 h-3.5" /> Copier
                </button>
                {r.has_valid_phone && (
                  <a href={r.wa_url} target="_blank" rel="noopener noreferrer"
                     onClick={() => markSent(r.registration_number)}
                     className="px-4 py-2 bg-emerald-500 text-white text-xs font-bold rounded-sm hover:bg-emerald-600 inline-flex items-center gap-1.5"
                     data-testid={`wa-send-${r.registration_number}`}>
                    <MessageCircle className="w-3.5 h-3.5" /> Envoyer WA
                  </a>
                )}
              </div>
            </div>
          ))}
          {recipients.length === 0 && (
            <div className="text-center text-foreground/55 py-10">Aucun destinataire.</div>
          )}
        </div>
      )}
    </div>
  );
}
